import React, { createContext, useCallback, useMemo } from "react";
import { isSupportedLocale, resolveLocaleDate } from "core/datetime";
import { Locale, format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const isoShortDateFormat = "yyyy-MM-dd";

interface LocaleContextType {
  /**
   * The locale of the current user, depending on their browser settings.
   * Defaults to null if user's locale is unsupported.
   */
  locale: null | Locale;
  /**
   * Formats the given date into a short date string based on the users browser settings.
   * @param date a date or date string to parse and format
   * @param showTime whether to show the time as part of the new date string
   */
  formatShort: (date: string | Date, showTime?: boolean) => string;
  /**
   * Formats the given date into a long date string based on the users browser settings
   * @param date a date or date string to parse and format
   * @param showTime whether to show the time as part of the new date string
   */
  formatLong: (date: string | Date, showTime?: boolean) => string;
  /**
   * The short date format based on the user's current locale.
   * Defaults to ISO format if the user is using an unsupported locale
   */
  shortDateFormat: string;
}

export const LocaleContext = createContext<LocaleContextType>({
  locale: null,
  formatShort: () => "",
  formatLong: () => "",
  // default to ISO Format
  shortDateFormat: isoShortDateFormat
});
LocaleContext.displayName = "LocaleContext";

/**
 * Provides locale information based on the users browser settings.
 * Also sets the MuiPickerUtilsProvider settings based on the locale.
 */
export const LocaleProvider = ({
  children
}: React.PropsWithChildren<{}>): JSX.Element => {
  const { locale, shortDateFormat } = useMemo(() => {
    const resolvedLocale = resolveLocaleDate();

    if (isSupportedLocale(resolvedLocale?.code)) {
      return {
        locale: resolvedLocale,
        shortDateFormat: resolvedLocale.formatLong.date({
          width: "short"
        })
      };
    }

    return {
      locale: null,
      shortDateFormat: isoShortDateFormat
    };
  }, []);

  const formatShort = useCallback(
    (date: string | Date, showTime: boolean = false): string => {
      const formatString = showTime ? "Pp" : "P";
      return format(new Date(date), formatString, { locale });
    },
    [locale]
  );

  const formatLong = useCallback(
    (date: string | Date, showTime: boolean = false): string => {
      const formatString = showTime ? "PPpp" : "PP";
      return format(new Date(date), formatString, { locale });
    },
    [locale]
  );

  return (
    <LocaleContext.Provider
      value={{
        locale,
        formatShort,
        formatLong,
        shortDateFormat
      }}
    >
      {/* https://mui.com/x/react-date-pickers/adapters-locale/#with-date-fns */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        {children}
      </LocalizationProvider>
    </LocaleContext.Provider>
  );
};
